import React, { useEffect, useState } from 'react';
import { getUsers, resetUserPassword, deleteUser, signup } from '../services/api';
import { KeyRound, Trash2, UserPlus, X, Eye, EyeOff } from 'lucide-react';

interface User {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    name: string;
    profile_url: string | null;
    role: string;
    isDeleted: boolean;
    createdAt: string;
    updatedAt: string;
}

const Users: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [total, setTotal] = useState(0);
    const [actionLoading, setActionLoading] = useState<number | null>(null);
    
    // Get current user's role
    const currentUserRole = localStorage.getItem('user_role') || '';
    const isSuperAdmin = currentUserRole === 'super_admin';

    // Add User Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newUser, setNewUser] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role: 'user'
    });
    const [showPassword, setShowPassword] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);
    const [modalLoading, setModalLoading] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getUsers(0, 100);
            setUsers(data.users);
            setTotal(data.total);
        } catch (err: any) {
            console.error('Failed to fetch users:', err);
            const detail = err.response?.data?.detail;
            const errorMsg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e.msg || e).join(', ') : 'Failed to load users');
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (userId: number, email: string) => {
        if (!window.confirm(`Are you sure you want to send a password reset email to ${email}?`)) {
            return;
        }

        try {
            setActionLoading(userId);
            await resetUserPassword(userId);
            alert(`Password reset email sent to ${email}`);
        } catch (err: any) {
            console.error('Failed to reset password:', err);
            alert(err.response?.data?.detail || 'Failed to reset password');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDeleteUser = async (userId: number, name: string) => {
        if (!window.confirm(`Are you sure you want to delete user ${name}? This action cannot be undone.`)) {
            return;
        }

        try {
            setActionLoading(userId);
            await deleteUser(userId);
            // Remove user from local state
            setUsers(users.filter(u => u.id !== userId));
            setTotal(total - 1);
        } catch (err: any) {
            console.error('Failed to delete user:', err);
            alert(err.response?.data?.detail || 'Failed to delete user');
        } finally {
            setActionLoading(null);
        }
    };

    const handleAddUser = async (e: React.FormEvent) => {
        e.preventDefault();
        setModalError(null);
        setModalLoading(true);

        try {
            await signup(newUser);
            setIsModalOpen(false);
            setNewUser({
                first_name: '',
                last_name: '',
                email: '',
                password: '',
                role: 'user'
            });
            fetchUsers(); // Refresh list
            alert('User created successfully');
        } catch (err: any) {
            console.error('Failed to create user:', err);
            setModalError(err.response?.data?.detail || 'Failed to create user');
        } finally {
            setModalLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const getRoleBadgeClass = (role: string) => {
        switch (role.toLowerCase()) {
            case 'super_admin':
                return 'badge-super-admin';
            case 'admin':
                return 'badge-admin';
            default:
                return 'badge-user';
        }
    };

    return (
        <div className="page-container">
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h1>Users Management</h1>
                    <p className="page-subtitle">Manage portal users and their roles</p>
                </div>
                <button className="btn-primary" onClick={() => setIsModalOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <UserPlus size={20} />
                    Add User
                </button>
            </div>

            <div className="content-card">
                {loading ? (
                    <div style={{ padding: '2rem', textAlign: 'center' }}>
                        <p>Loading users...</p>
                    </div>
                ) : error ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--error)' }}>
                        <p>{error}</p>
                        <button onClick={fetchUsers} className="btn-primary" style={{ marginTop: '1rem' }}>
                            Retry
                        </button>
                    </div>
                ) : (
                    <>
                        <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)' }}>
                            <h3>Total Users: {total}</h3>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table className="users-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Role</th>
                                        <th>Created At</th>
                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                                                No users found
                                            </td>
                                        </tr>
                                    ) : (
                                        users.map((user) => (
                                            <tr key={user.id}>
                                                <td>
                                                    <div>
                                                        <strong>{user.name}</strong>
                                                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                                            {user.first_name} {user.last_name}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{user.email}</td>
                                                <td>
                                                    <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                                                        {user.role}
                                                    </span>
                                                </td>
                                                <td>{formatDate(user.createdAt)}</td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <div className="action-buttons" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                                                        <button
                                                            className="icon-btn tooltip"
                                                            data-tooltip="Reset Password"
                                                            onClick={() => handleResetPassword(user.id, user.email)}
                                                            disabled={actionLoading === user.id}
                                                        >
                                                            <KeyRound size={18} />
                                                        </button>
                                                        <button
                                                            className="icon-btn delete tooltip"
                                                            data-tooltip="Delete User"
                                                            onClick={() => handleDeleteUser(user.id, user.name)}
                                                            disabled={actionLoading === user.id}
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {/* Add User Modal */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h2>Add New User</h2>
                            <button onClick={() => setIsModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                <X size={24} />
                            </button>
                        </div>

                        {modalError && (
                            <div style={{ backgroundColor: '#fef2f2', color: '#ef4444', padding: '0.75rem', borderRadius: '0.375rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
                                {modalError}
                            </div>
                        )}

                        <form onSubmit={handleAddUser}>
                            <div className="form-group">
                                <label>First Name</label>
                                <input
                                    type="text"
                                    required
                                    value={newUser.first_name}
                                    onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Last Name</label>
                                <input
                                    type="text"
                                    required
                                    value={newUser.last_name}
                                    onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    required
                                    value={newUser.email}
                                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Password</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        required
                                        minLength={8}
                                        value={newUser.password}
                                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{
                                            position: 'absolute',
                                            right: '0.5rem',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: 'var(--text-secondary)'
                                        }}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Role</label>
                                <select
                                    value={newUser.role}
                                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: 'var(--radius-md)'
                                    }}
                                >
                                    <option value="user">User</option>
                                    {isSuperAdmin && <option value="admin">Admin</option>}
                                </select>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
                                <button
                                    type="button"
                                    className="secondary"
                                    onClick={() => setIsModalOpen(false)}
                                    disabled={modalLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={modalLoading}
                                >
                                    {modalLoading ? 'Creating...' : 'Create User'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;
